import React, { useEffect, useState, createElement, Component } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, Copy, FileText, Clock, AlertCircle, RefreshCw, ImageIcon, Link as LinkIcon, Download, Share2, Calendar, User, Heart, MessageSquare, Repeat2, Tag, Monitor, Film, Info, Languages, Languages as LanguagesIcon, Layers, Code, ExternalLink, Play, Quote, BrainCircuit, ChevronDown, ChevronUp, Twitter, Check, Menu, X } from 'lucide-react';
import { getTaskStatus, getTranscript, parseTranscript, getTranscriptWithFormat, getSimilarTranscriptions, getPublicTranscriptPageUrl, getTranscriptionById, deleteTranscription, retryTask } from '../lib/api';
import { TranscriptionResponse, TranscriptionResult, TranscriptFormat } from '../lib/types';
import { TranscriptionCard } from '../components/transcription/TranscriptionCard';
import { ShareTranscript } from '../components/transcription/ShareTranscript';
import { MetaTags } from '../components/MetaTags';
import { ResearchTools } from '../components/research/ResearchTools';
import { TranscriptChatWidget } from '../components/transcription/TranscriptChatWidget';
export function TranscriptionDetail() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const [copied, setCopied] = useState(false);
  const [task, setTask] = useState<TranscriptionResponse | null>(null);
  const [transcript, setTranscript] = useState<TranscriptionResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [processingStartTime, setProcessingStartTime] = useState<number | null>(null);
  const [elapsedTime, setElapsedTime] = useState<string>('00:00');
  const [showShareOptions, setShowShareOptions] = useState(false);
  const [showTechnicalDetails, setShowTechnicalDetails] = useState(false);
  const [similarTranscriptions, setSimilarTranscriptions] = useState<TranscriptionResponse[]>([]);
  const [showFullTranscript, setShowFullTranscript] = useState(true);
  const [quoteCopied, setQuoteCopied] = useState(false);
  // Mobile specific states
  const [showSecondaryInfo, setShowSecondaryInfo] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [showBottomBar, setShowBottomBar] = useState(true);
  // Handle scroll for sticky bottom bar
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setShowBottomBar(false); // Hide on scroll down
      } else {
        setShowBottomBar(true); // Show on scroll up
      }
      setLastScrollY(currentScrollY);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);
  // Helper function to parse TLDR from string to array
  const parseTLDR = (tldrData?: string | string[]): string[] => {
    if (!tldrData) return [];
    // If tldrData is already an array, return it
    if (Array.isArray(tldrData)) {
      return tldrData;
    }
    // Otherwise try to parse it as a JSON string
    try {
      return JSON.parse(tldrData);
    } catch {
      return [];
    }
  };
  const getThumbnailUrl = () => {
    if (task && task.status === 'completed') {
      return task.supabase_thumbnail_url || task.square_thumbnail_url || task.thumbnail_url || task.thumbnail;
    }
    return null;
  };
  const renderPlaceholder = () => {
    if (!task) return null;
    const title = task.title || 'Untitled Video';
    const platform = task.platform || 'TikTok';
    const videoId = task.video_id || task.task_id || 'Unknown';
    return <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-gray-800 to-gray-950 relative" data-id="element-2416">
        <div className="absolute inset-0 opacity-10" data-id="element-2417">
          <div className="absolute top-1/4 left-1/4 w-1/2 h-1/2 rounded-full bg-primary/20" data-id="element-2418"></div>
          <div className="absolute bottom-1/4 right-1/4 w-1/3 h-1/3 rounded-full bg-primary/30" data-id="element-2419"></div>
        </div>
        <div className="z-10 flex flex-col items-center justify-center p-6 text-center" data-id="element-2420">
          <Film size={36} className="text-gray-400 mb-4" data-id="element-2421" />
          <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mb-4" data-id="element-2422">
            <span className="text-2xl font-bold text-white" data-id="element-2423">
              {title.substring(0, 2).toUpperCase()}
            </span>
          </div>
          <h3 className="text-white/90 text-lg font-medium mb-2 max-w-xs" data-id="element-2424">
            {title}
          </h3>
          <p className="text-white/60 text-sm mb-1" data-id="element-2425">{platform} Transcript</p>
          <p className="text-white/40 text-xs" data-id="element-2426">
            ID: {videoId.substring(0, 12)}...
          </p>
          {task.duration && <div className="mt-4 bg-white/10 px-3 py-1 rounded-full flex items-center" data-id="element-2427">
              <Clock size={14} className="text-primary mr-2" data-id="element-2428" />
              <span className="text-white/70 text-sm" data-id="element-2429">
                {formatDuration(task.duration)}
              </span>
            </div>}
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-1/6 bg-gradient-to-t from-black/40 to-transparent" data-id="element-2430"></div>
      </div>;
  };
  const getMetaTitle = () => {
    if (!task) return 'Transcript | ScribeTok';
    return `TikTok Transcript: "${task.title || 'Untitled Video'}" | ScribeTok`;
  };
  const getMetaDescription = () => {
    if (!task) return 'View the full transcript of this TikTok video on ScribeTok.';
    // Use the quote for the description if available
    if (task.quote) {
      return `"${task.quote}" - See the full transcript for this TikTok video on ScribeTok.`;
    }
    let description = `See the full transcript for this TikTok video`;
    // Add engagement metrics if available
    if (task.like_count || task.comment_count) {
      description += ' (';
      if (task.like_count) description += `${task.like_count.toLocaleString()} likes`;
      if (task.like_count && task.comment_count) description += ', ';
      if (task.comment_count) description += `${task.comment_count.toLocaleString()} comments`;
      description += ')';
    }
    description += ' on ScribeTok.';
    // Add a snippet of the transcript if available
    if (transcript && transcript.text) {
      const snippet = transcript.text.substring(0, 100).trim();
      description += ` "${snippet}${transcript.text.length > 100 ? '...' : ''}"`;
    }
    return description;
  };
  const getMetaImageUrl = () => {
    if (!task) return undefined;
    return getThumbnailUrl() || undefined;
  };
  const getMetaUrl = () => {
    if (!id) return window.location.href;
    return getPublicTranscriptPageUrl(id);
  };
  useEffect(() => {
    const fetchTaskData = async () => {
      if (!id) return;
      try {
        setIsLoading(true);
        setError(null);
        const taskData = await getTaskStatus(id);
        setTask(taskData);
        if (taskData.status === 'completed') {
          try {
            // Always get transcript in text format
            const rawTranscript = (await getTranscriptWithFormat(id, 'text')) as string;
            const parsedTranscript = parseTranscript(rawTranscript);
            setTranscript(parsedTranscript);
            // Get similar transcriptions
            const similar = await getSimilarTranscriptions(id, 4);
            setSimilarTranscriptions(similar);
          } catch (err) {
            console.error('Error fetching transcript data:', err);
          }
          setIsPolling(false);
          setProcessingStartTime(null);
        } else if (taskData.status === 'processing' || taskData.status === 'pending') {
          setIsPolling(true);
          if (!processingStartTime) {
            setProcessingStartTime(Date.now());
          }
        } else if (taskData.status === 'failed') {
          setError(taskData.error || 'Transcription failed');
          setIsPolling(false);
          setProcessingStartTime(null);
        }
      } catch (err) {
        console.error('Error fetching task data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load transcription');
        setIsPolling(false);
      } finally {
        setIsLoading(false);
      }
    };
    fetchTaskData();
    let intervalId: NodeJS.Timeout;
    if (isPolling) {
      intervalId = setInterval(() => {
        fetchTaskData();
      }, 5000);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [id, isPolling, processingStartTime]);
  useEffect(() => {
    if (!processingStartTime) return;
    const intervalId = setInterval(() => {
      const now = Date.now();
      const elapsed = Math.floor((now - processingStartTime) / 1000); // elapsed time in seconds
      const minutes = Math.floor(elapsed / 60).toString().padStart(2, '0');
      const seconds = (elapsed % 60).toString().padStart(2, '0');
      setElapsedTime(`${minutes}:${seconds}`);
    }, 1000);
    return () => clearInterval(intervalId);
  }, [processingStartTime]);
  const handleCopyTranscript = () => {
    if (!transcript) return;
    navigator.clipboard.writeText(transcript.text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const handleCopyQuote = () => {
    if (!task || !task.quote) return;
    navigator.clipboard.writeText(`"${task.quote}" - ${task.title || 'TikTok Video'}`);
    setQuoteCopied(true);
    setTimeout(() => setQuoteCopied(false), 2000);
  };
  const handleShareQuote = () => {
    if (!task || !task.quote) return;
    const shareText = `"${task.quote}" - ${task.title || 'TikTok Video'}`;
    const shareUrl = getPublicTranscriptPageUrl(task.task_id);
    if (navigator.share) {
      navigator.share({
        text: shareText,
        url: shareUrl
      }).catch(err => {
        console.error('Share failed:', err);
      });
    } else {
      // Fallback if Web Share API is not available
      window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, '_blank');
    }
  };
  const handleDownload = () => {
    if (!transcript || !task) return;
    const content = transcript.text;
    const blob = new Blob([content], {
      type: 'text/plain'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transcript-${task.video_id || task.task_id}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  const handleDownloadSrt = () => {
    if (!task) return;
    getTranscriptWithFormat(task.task_id, 'srt').then(content => {
      const blob = new Blob([content as string], {
        type: 'text/srt'
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `transcript-${task.video_id || task.task_id}.srt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }).catch(err => console.error('Error downloading SRT:', err));
  };
  const handleSharePublicLink = () => {
    if (!task) return;
    const publicUrl = getPublicTranscriptPageUrl(task.task_id);
    navigator.clipboard.writeText(publicUrl);
    alert('Public link copied to clipboard!');
  };
  const handleShare = () => {
    if (navigator.share && transcript && task) {
      navigator.share({
        title: task?.title || 'Video Transcript',
        text: transcript.text.substring(0, 100) + '...',
        url: getPublicTranscriptPageUrl(task.task_id)
      }).catch(err => {
        console.error('Share failed:', err);
        setShowShareOptions(!showShareOptions);
      });
    } else {
      setShowShareOptions(!showShareOptions);
    }
  };
  const handleRetry = async () => {
    if (!id) return;
    try {
      setIsLoading(true);
      setError(null);
      // Call the retry API
      const retryResponse = await retryTask(id);
      console.log('Retry initiated:', retryResponse);
      // Update local state to show processing
      setTask(prev => prev ? {
        ...prev,
        status: 'pending'
      } : null);
      // Start polling for status updates
      setIsPolling(true);
      setProcessingStartTime(Date.now());
    } catch (err) {
      console.error('Error retrying task:', err);
      setError(err instanceof Error ? err.message : 'Failed to retry transcription');
    } finally {
      setIsLoading(false);
    }
  };
  const formatDuration = (seconds?: number) => {
    if (!seconds) return 'Unknown';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    if (mins < 1) {
      return `${secs}s`;
    }
    return `${mins}m ${secs}s`;
  };
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'Unknown';
    // Handle YYYYMMDD format
    if (/^\d{8}$/.test(dateStr)) {
      const year = dateStr.substring(0, 4);
      const month = dateStr.substring(4, 6);
      const day = dateStr.substring(6, 8);
      return new Date(`${year}-${month}-${day}`).toLocaleDateString();
    }
    // Handle regular date strings
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch (e) {
      return dateStr;
    }
  };
  if (isLoading) {
    return <div className="container py-4 md:py-8 px-4 md:px-6" data-id="element-2431">
        <div className="flex items-center mb-4 md:mb-6" data-id="element-2432">
          <Link to="/app/transcriptions" className="mr-2 md:mr-4" data-id="element-2433">
            <Button variant="ghost" size="sm" className="h-11 px-3 md:h-9 md:px-3" data-id="element-2434">
              <ArrowLeft size={18} className="mr-2" data-id="element-2435" />
              <span className="hidden sm:inline" data-id="element-2436">Back</span>
            </Button>
          </Link>
          <h1 className="text-xl md:text-2xl font-bold truncate" data-id="element-2437">
            Loading Transcription...
          </h1>
        </div>
        <Card className="p-8 md:p-12 flex justify-center items-center" data-id="element-2438">
          <div className="flex flex-col items-center" data-id="element-2439">
            <RefreshCw size={32} className="animate-spin text-primary mb-4" data-id="element-2440" />
            <p className="text-muted-foreground" data-id="element-2441">
              Loading transcription data...
            </p>
          </div>
        </Card>
      </div>;
  }
  if (error || !task) {
    return <div className="container py-4 md:py-8 px-4 md:px-6" data-id="element-2442">
        <div className="flex items-center mb-4 md:mb-6" data-id="element-2443">
          <Link to="/app/transcriptions" className="mr-2 md:mr-4" data-id="element-2444">
            <Button variant="ghost" size="sm" className="h-11 px-3 md:h-9 md:px-3" data-id="element-2445">
              <ArrowLeft size={18} className="mr-2" data-id="element-2446" />
              <span className="hidden sm:inline" data-id="element-2447">Back</span>
            </Button>
          </Link>
          <h1 className="text-xl md:text-2xl font-bold truncate" data-id="element-2448">Error</h1>
        </div>
        <Card className="p-8 md:p-12 border-destructive/20 bg-destructive/5" data-id="element-2449">
          <div className="flex flex-col items-center text-center" data-id="element-2450">
            <AlertCircle size={32} className="text-destructive mb-4" data-id="element-2451" />
            <h2 className="text-xl font-semibold mb-2" data-id="element-2452">
              Failed to Load Transcription
            </h2>
            <p className="text-muted-foreground mb-6" data-id="element-2453">
              {error || 'Transcription not found or an error occurred'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4" data-id="element-2454">
              <Link to="/app/transcriptions" data-id="element-2455">
                <Button variant="outline" className="w-full sm:w-auto h-12" data-id="element-2456">
                  Back to Transcriptions
                </Button>
              </Link>
              <Button onClick={handleRetry} className="w-full sm:w-auto h-12" data-id="element-2457">
                Retry
              </Button>
            </div>
          </div>
        </Card>
      </div>;
  }
  if (task && (task.status === 'processing' || task.status === 'pending')) {
    return <div className="container py-4 md:py-8 px-4 md:px-6" data-id="element-2458">
        <div className="flex items-center mb-4 md:mb-6" data-id="element-2459">
          <Link to="/app/transcriptions" className="mr-2 md:mr-4" data-id="element-2460">
            <Button variant="ghost" size="sm" className="h-11 px-3 md:h-9 md:px-3" data-id="element-2461">
              <ArrowLeft size={18} className="mr-2" data-id="element-2462" />
              <span className="hidden sm:inline" data-id="element-2463">Back</span>
            </Button>
          </Link>
          <h1 className="text-xl md:text-2xl font-bold truncate" data-id="element-2464">
            Processing Transcription
          </h1>
        </div>
        <Card className="p-8 md:p-12" data-id="element-2465">
          <div className="flex flex-col items-center text-center" data-id="element-2466">
            <RefreshCw size={32} className="animate-spin text-blue-500 mb-4" data-id="element-2467" />
            <h2 className="text-xl font-semibold mb-2" data-id="element-2468">
              Your Video is Being Processed
            </h2>
            <p className="text-gray-500 mb-2" data-id="element-2469">
              This may take a few minutes depending on the length of the video.
            </p>
            {processingStartTime && <p className="text-blue-500 font-medium mb-6" data-id="element-2470">
                Processing time: {elapsedTime}
              </p>}
            <div className="w-full max-w-md mb-6" data-id="element-2471">
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-2472">
                <div className="h-2 bg-blue-500 rounded-full animate-pulse w-full" data-id="element-2473"></div>
              </div>
            </div>
            <Button variant="outline" onClick={() => window.location.reload()} className="flex items-center h-12" data-id="element-2474">
              <RefreshCw size={16} className="mr-2" data-id="element-2475" />
              Refresh Status
            </Button>
          </div>
        </Card>
      </div>;
  }
  // Parse TLDR if available
  const tldrItems = task.tldr ? parseTLDR(task.tldr) : [];
  const hasInsights = task.quote || tldrItems && tldrItems.length > 0;
  return <div className="container py-4 md:py-8 px-4 md:px-6 pb-24 md:pb-8" data-id="element-2476">
      {task && <MetaTags title={getMetaTitle()} description={getMetaDescription()} imageUrl={getMetaImageUrl()} url={getMetaUrl()} data-id="element-2477" />}

      <div className="flex items-center mb-4 md:mb-6" data-id="element-2478">
        <Link to="/app/transcriptions" className="mr-2 md:mr-4" data-id="element-2479">
          <Button variant="ghost" size="sm" className="h-11 px-3 md:h-9 md:px-3 rounded-full" data-id="element-2480">
            <ArrowLeft size={18} className="mr-1 md:mr-2" data-id="element-2481" />
            <span className="hidden sm:inline" data-id="element-2482">Back</span>
          </Button>
        </Link>
        <h1 className="text-xl md:text-2xl font-bold truncate" data-id="element-2483">
          {task?.title || 'Untitled Video'}
        </h1>
      </div>

      {/* Mobile Sticky Bottom Action Bar - No longer needs chat sheet state check */}
      <div className={`fixed bottom-0 left-0 right-0 bg-background/95 backdrop-blur-md border-t p-4 z-50 md:hidden transition-transform duration-300 ${showBottomBar ? 'translate-y-0' : 'translate-y-full'}`} data-id="element-2484">
        <div className="flex gap-3 justify-between max-w-md mx-auto" data-id="element-2485">
          <Button variant="outline" className="flex-1 justify-center items-center rounded-full h-12" onClick={handleCopyTranscript} disabled={!transcript} data-id="element-2486">
            <Copy size={18} className="mr-2" data-id="element-2487" />
            Copy
          </Button>
          <Button variant="outline" className="flex-1 justify-center items-center rounded-full h-12" onClick={handleDownload} disabled={!transcript} data-id="element-2488">
            <Download size={18} className="mr-2" data-id="element-2489" />
            Save
          </Button>
          <Button variant="default" className="flex-1 justify-center items-center rounded-full h-12" onClick={handleShare} disabled={!transcript} data-id="element-2490">
            <Share2 size={18} className="mr-2" data-id="element-2491" />
            Share
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6" data-id="element-2492">
        {/* Left Column (Video & Metadata) */}
        <div className="lg:col-span-1 space-y-4 md:space-y-6" data-id="element-2493">
          {/* Video Preview Card - Always Visible */}
          <Card className="overflow-hidden border-0 shadow-md" data-id="element-2494">
            <div className="w-full aspect-video bg-gray-900 flex items-center justify-center overflow-hidden relative" data-id="element-2495">
              {task.status === 'completed' ? <div className="relative w-full h-full" data-id="element-2496">
                  {getThumbnailUrl() ? <>
                      {/* Blurred background layer */}
                      <div className="absolute inset-0 z-0" style={{
                  backgroundImage: `url(${getThumbnailUrl()})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  filter: 'blur(40px)',
                  transform: 'scale(1.1)'
                }} data-id="element-2497" />

                      {/* Dark overlay for better contrast */}
                      <div className="absolute inset-0 bg-black/40 z-[1]" data-id="element-2498" />

                      {/* Main image - fills the card */}
                      <img src={getThumbnailUrl() || ''} alt={task.title || 'Video thumbnail'} className="absolute inset-0 w-full h-full object-cover z-[2]" onError={e => {
                  e.currentTarget.style.display = 'none';
                  const container = e.currentTarget.parentElement;
                  if (container) {
                    const placeholder = container.querySelector('.placeholder-fallback');
                    if (placeholder) {
                      ;
                      (placeholder as HTMLElement).style.display = 'flex';
                    }
                  }
                }} data-id="element-2499" />

                      {/* Fallback placeholder (hidden by default) */}
                      <div className="placeholder-fallback absolute inset-0 hidden w-full h-full flex-col items-center justify-center bg-gradient-to-br from-gray-800 to-gray-950 z-[2]" data-id="element-2500">
                        <div className="z-10 flex flex-col items-center justify-center p-6 text-center" data-id="element-2501">
                          <Film size={36} className="text-gray-400 mb-4" data-id="element-2502" />
                          <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mb-4" data-id="element-2503">
                            <span className="text-2xl font-bold text-white" data-id="element-2504">
                              {(task.title || 'UV').substring(0, 2).toUpperCase()}
                            </span>
                          </div>
                          <h3 className="text-white/90 text-lg font-medium mb-2 max-w-xs" data-id="element-2505">
                            {task.title || 'Untitled Video'}
                          </h3>
                        </div>
                      </div>
                    </> : renderPlaceholder()}

                  {/* Play button overlay */}
                  {task.url && <div className="absolute inset-0 flex items-center justify-center z-[20] cursor-pointer bg-black/20 hover:bg-black/30 transition-colors" onClick={() => {
                const videoContainer = document.createElement('div');
                videoContainer.className = 'fixed inset-0 bg-black/90 flex items-center justify-center z-50';
                videoContainer.style.backdropFilter = 'blur(5px)';
                const videoElement = document.createElement('video');
                videoElement.className = 'w-full max-w-4xl max-h-[90vh]';
                videoElement.controls = true;
                videoElement.autoplay = true;
                videoElement.src = task.url;
                const closeButton = document.createElement('button');
                closeButton.className = 'absolute top-4 right-4 text-white bg-white/10 rounded-full p-3 hover:bg-white/20 transition-colors';
                closeButton.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>`;
                closeButton.onclick = () => {
                  document.body.removeChild(videoContainer);
                };
                videoContainer.appendChild(videoElement);
                videoContainer.appendChild(closeButton);
                document.body.appendChild(videoContainer);
                videoContainer.onclick = e => {
                  if (e.target === videoContainer) {
                    document.body.removeChild(videoContainer);
                  }
                };
              }} data-id="element-2506">
                      <div className="w-16 h-16 rounded-full bg-primary/90 flex items-center justify-center hover:bg-primary transition-colors shadow-lg backdrop-blur-sm" data-id="element-2507">
                        <Play size={32} className="text-white ml-1" data-id="element-2508" />
                      </div>
                    </div>}
                </div> : <div className="flex flex-col items-center justify-center" data-id="element-2509">
                  <ImageIcon size={24} className="text-gray-400 mb-2" data-id="element-2510" />
                  <span className="text-gray-500 font-medium" data-id="element-2511">
                    {task.video_id ? `Video ID: ${task.video_id}` : 'Thumbnail not available'}
                  </span>
                </div>}
            </div>
            <CardContent className="p-5" data-id="element-2512">
              <h3 className="font-semibold text-lg md:text-xl mb-3 leading-snug" data-id="element-2513">
                {task.title || 'Untitled Video'}
              </h3>
              {/* Basic Video Info */}
              <div className="flex flex-wrap gap-3 text-sm text-muted-foreground mb-4" data-id="element-2514">
                <span className="flex items-center bg-secondary/50 px-2 py-1 rounded-md" data-id="element-2515">
                  <Clock size={14} className="mr-1.5" data-id="element-2516" />
                  {task.duration ? formatDuration(task.duration) : transcript && transcript.segments.length > 0 ? `${Math.floor(transcript.segments[transcript.segments.length - 1].end / 60)}:${Math.floor(transcript.segments[transcript.segments.length - 1].end % 60).toString().padStart(2, '0')}` : '00:00'}
                </span>
                <span className="flex items-center bg-secondary/50 px-2 py-1 rounded-md" data-id="element-2517">
                  <Calendar size={14} className="mr-1.5" data-id="element-2518" />
                  {task.upload_date ? formatDate(task.upload_date) : new Date(task.created_at).toLocaleDateString()}
                </span>
                {task.platform && <span className="flex items-center bg-secondary/50 px-2 py-1 rounded-md capitalize" data-id="element-2519">
                    <Film size={14} className="mr-1.5" data-id="element-2520" />
                    {task.platform}
                  </span>}
              </div>

              {/* View Original Button */}
              <div className="space-y-3" data-id="element-2521">
                {(task.url || task.uploader_url || task.video_id) && <a href={task.url} target="_blank" rel="noopener noreferrer" className="block w-full" data-id="element-2522">
                    <Button variant="tiktok" className="w-full flex items-center justify-center h-12 text-base font-medium" data-id="element-2523">
                      <LinkIcon size={18} className="mr-2" data-id="element-2524" />
                      View Original Video
                    </Button>
                  </a>}
              </div>
            </CardContent>
          </Card>

          {/* Secondary Info Wrapper - Collapsible on Mobile, Visible on Desktop */}
          <div className="space-y-4 md:space-y-6" data-id="element-2525">
            {/* Mobile Toggle for Secondary Info */}
            <Button variant="outline" className="w-full flex justify-between items-center md:hidden h-14 text-base" onClick={() => setShowSecondaryInfo(!showSecondaryInfo)} data-id="element-2526">
              <span className="flex items-center" data-id="element-2527">
                <Info size={18} className="mr-2 text-primary" data-id="element-2528" />
                Video Details & Stats
              </span>
              {showSecondaryInfo ? <ChevronUp size={18} data-id="element-2529" /> : <ChevronDown size={18} data-id="element-2530" />}
            </Button>

            <div className={`space-y-4 md:space-y-6 ${showSecondaryInfo ? 'block' : 'hidden md:block'}`} data-id="element-2531">
              {/* Creator Info Card */}
              {(task.channel || task.uploader || task.channel_id) && <Card data-id="element-2532">
                  <CardContent className="p-5" data-id="element-2533">
                    <h3 className="font-medium mb-4 flex items-center text-base" data-id="element-2534">
                      <User size={18} className="mr-2 text-primary" data-id="element-2535" />
                      Creator Information
                    </h3>
                    <div className="space-y-3" data-id="element-2536">
                      {task.uploader && <div className="flex justify-between items-center p-2 bg-secondary/30 rounded-lg" data-id="element-2537">
                          <span className="text-sm text-muted-foreground" data-id="element-2538">
                            Username
                          </span>
                          <span className="text-sm font-medium truncate max-w-[200px]" data-id="element-2539">
                            {task.uploader}
                          </span>
                        </div>}
                      {task.channel && <div className="flex justify-between items-center p-2 bg-secondary/30 rounded-lg" data-id="element-2540">
                          <span className="text-sm text-muted-foreground" data-id="element-2541">
                            Channel
                          </span>
                          <span className="text-sm font-medium truncate max-w-[200px]" data-id="element-2542">
                            {task.channel}
                          </span>
                        </div>}
                      {task.uploader_url && <div className="mt-2" data-id="element-2543">
                          <a href={task.uploader_url} target="_blank" rel="noopener noreferrer" className="text-primary text-sm hover:underline flex items-center justify-center p-2 bg-primary/5 rounded-lg font-medium" data-id="element-2544">
                            <LinkIcon size={14} className="mr-2" data-id="element-2545" />
                            View Creator Profile
                          </a>
                        </div>}
                    </div>
                  </CardContent>
                </Card>}

              {/* Engagement Metrics Card */}
              <Card data-id="element-2546">
                <CardContent className="p-5" data-id="element-2547">
                  <h3 className="font-medium mb-4 flex items-center text-base" data-id="element-2548">
                    <Heart size={18} className="mr-2 text-primary" data-id="element-2549" />
                    Engagement Metrics
                  </h3>
                  <div className="grid grid-cols-3 gap-3 text-center" data-id="element-2550">
                    <div className="p-3 bg-secondary/50 rounded-xl" data-id="element-2551">
                      <div className="flex justify-center mb-1.5" data-id="element-2552">
                        <Heart size={20} className="text-primary" data-id="element-2553" />
                      </div>
                      <span className="block text-base font-bold" data-id="element-2554">
                        {task.like_count?.toLocaleString() || '—'}
                      </span>
                      <span className="block text-xs text-muted-foreground mt-0.5" data-id="element-2555">
                        Likes
                      </span>
                    </div>
                    <div className="p-3 bg-secondary/50 rounded-xl" data-id="element-2556">
                      <div className="flex justify-center mb-1.5" data-id="element-2557">
                        <MessageSquare size={20} className="text-primary" data-id="element-2558" />
                      </div>
                      <span className="block text-base font-bold" data-id="element-2559">
                        {task.comment_count?.toLocaleString() || '—'}
                      </span>
                      <span className="block text-xs text-muted-foreground mt-0.5" data-id="element-2560">
                        Comments
                      </span>
                    </div>
                    <div className="p-3 bg-secondary/50 rounded-xl" data-id="element-2561">
                      <div className="flex justify-center mb-1.5" data-id="element-2562">
                        <Repeat2 size={20} className="text-primary" data-id="element-2563" />
                      </div>
                      <span className="block text-base font-bold" data-id="element-2564">
                        {task.repost_count?.toLocaleString() || '—'}
                      </span>
                      <span className="block text-xs text-muted-foreground mt-0.5" data-id="element-2565">
                        Reposts
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Tags Card */}
              {task.auto_tags && task.auto_tags.length > 0 && <Card data-id="element-2566">
                  <CardContent className="p-5" data-id="element-2567">
                    <h3 className="font-medium mb-3 flex items-center text-base" data-id="element-2568">
                      <Tag size={18} className="mr-2 text-primary" data-id="element-2569" />
                      Tags
                    </h3>
                    <div className="flex flex-wrap gap-2" data-id="element-2570">
                      {task.auto_tags.map((tag, index) => <span key={index} className="inline-block px-3 py-1.5 bg-secondary text-secondary-foreground rounded-full text-sm" data-id="element-2571">
                          #{tag}
                        </span>)}
                    </div>
                  </CardContent>
                </Card>}

              {/* Technical Details Card */}
              {(task.resolution || task.width || task.height || task.vcodec || task.acodec) && <Card data-id="element-2572">
                  <CardContent className="p-5" data-id="element-2573">
                    <div className="flex justify-between items-center mb-3" data-id="element-2574">
                      <h3 className="font-medium flex items-center text-base" data-id="element-2575">
                        <Monitor size={18} className="mr-2 text-muted-foreground" data-id="element-2576" />
                        Technical Details
                      </h3>
                      <Button variant="ghost" size="sm" onClick={() => setShowTechnicalDetails(!showTechnicalDetails)} className="h-8 px-2 text-xs" data-id="element-2577">
                        {showTechnicalDetails ? 'Hide' : 'Show'}
                      </Button>
                    </div>
                    {showTechnicalDetails ? <div className="space-y-2 text-sm" data-id="element-2578">
                        {task.resolution && <div className="flex justify-between p-2 bg-secondary/20 rounded" data-id="element-2579">
                            <span className="text-muted-foreground" data-id="element-2580">
                              Resolution
                            </span>
                            <span className="font-mono" data-id="element-2581">{task.resolution}</span>
                          </div>}
                        {(task.width || task.height) && <div className="flex justify-between p-2 bg-secondary/20 rounded" data-id="element-2582">
                            <span className="text-muted-foreground" data-id="element-2583">
                              Dimensions
                            </span>
                            <span className="font-mono" data-id="element-2584">
                              {task.width || '?'}×{task.height || '?'}
                            </span>
                          </div>}
                        {task.aspect_ratio && <div className="flex justify-between p-2 bg-secondary/20 rounded" data-id="element-2585">
                            <span className="text-muted-foreground" data-id="element-2586">
                              Aspect Ratio
                            </span>
                            <span className="font-mono" data-id="element-2587">
                              {task.aspect_ratio.toFixed(2)}
                            </span>
                          </div>}
                        {task.filesize && <div className="flex justify-between p-2 bg-secondary/20 rounded" data-id="element-2588">
                            <span className="text-muted-foreground" data-id="element-2589">
                              File Size
                            </span>
                            <span className="font-mono" data-id="element-2590">
                              {(task.filesize / (1024 * 1024)).toFixed(2)} MB
                            </span>
                          </div>}
                      </div> : <div className="text-center py-2 text-sm text-muted-foreground" data-id="element-2591">
                        Click "Show" to view technical specifications
                      </div>}
                  </CardContent>
                </Card>}

              {/* Desktop Download Options */}
              <Card className="hidden md:block" data-id="element-2592">
                <CardContent className="p-5" data-id="element-2593">
                  <h3 className="font-medium mb-3" data-id="element-2594">Download Transcript</h3>
                  <div className="space-y-2" data-id="element-2595">
                    <Button variant="outline" className="w-full justify-start rounded-lg h-11" onClick={handleDownload} disabled={!transcript} data-id="element-2596">
                      <FileText size={18} className="mr-2" data-id="element-2597" />
                      Download as TXT
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        {/* Right Column (Main Content) */}
        <div className="lg:col-span-2 space-y-4 md:space-y-6" data-id="element-2598">
          {/* Chat Widget - Show if we have ANY content to chat about (including just title) */}
          {task && task.task_id && task.status === 'completed' && (transcript?.text || task.description || task.title || task.quote || task.tldr && parseTLDR(task.tldr).length > 0) && <TranscriptChatWidget transcriptId={task.task_id} transcript={{
          content: transcript?.text || task.description || task.title || '',
          title: task.title || undefined,
          description: task.description || undefined,
          channel_name: task.channel || task.uploader || undefined,
          platform: task.platform || undefined,
          view_count: task.view_count || undefined,
          like_count: task.like_count || undefined,
          comment_count: task.comment_count || undefined,
          repost_count: task.repost_count || undefined,
          upload_date: task.upload_date || undefined,
          duration: task.duration || undefined,
          quote: task.quote || undefined,
          tldr: tldrItems
        }} data-id="element-2599" />}

          {/* Insights Card with Quote and TLDR */}
          {hasInsights && <Card className="border-0 shadow-sm md:border md:shadow-sm" data-id="element-2600">
              <div className="flex items-center justify-between p-4 md:p-5 border-b border-border/40" data-id="element-2601">
                <h2 className="font-semibold text-base md:text-lg flex items-center" data-id="element-2602">
                  <BrainCircuit size={20} className="mr-2 text-primary" data-id="element-2603" />
                  Insights
                </h2>
                {task.quote && <div className="flex items-center space-x-1" data-id="element-2604">
                    <Button variant="ghost" size="sm" onClick={handleCopyQuote} className="h-9 px-3 text-xs rounded-full" data-id="element-2605">
                      {quoteCopied ? <Check size={16} className="mr-1" data-id="element-2606" /> : <Copy size={16} className="mr-1" data-id="element-2607" />}
                      {quoteCopied ? 'Copied' : 'Copy'}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={handleShareQuote} className="h-9 px-3 text-xs rounded-full" data-id="element-2608">
                      <Twitter size={16} className="mr-1" data-id="element-2609" />
                      Share
                    </Button>
                  </div>}
              </div>
              <div className="p-5 md:p-6" data-id="element-2610">
                {/* Quote Section */}
                {task.quote && <div className="mb-6" data-id="element-2611">
                    <div className="flex items-center mb-3" data-id="element-2612">
                      <Quote size={18} className="mr-2 text-primary" data-id="element-2613" />
                      <h3 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground" data-id="element-2614">
                        Key Quote
                      </h3>
                    </div>
                    <blockquote className="border-l-4 border-primary/30 pl-4 py-1 text-lg md:text-xl font-medium italic text-foreground/90 leading-relaxed" data-id="element-2615">
                      "{task.quote}"
                    </blockquote>
                  </div>}
                {/* TLDR Section */}
                {tldrItems && tldrItems.length > 0 && <div data-id="element-2616">
                    <div className="flex items-center mb-3" data-id="element-2617">
                      <FileText size={18} className="mr-2 text-primary" data-id="element-2618" />
                      <h3 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground" data-id="element-2619">
                        Key Takeaways
                      </h3>
                    </div>
                    <ul className="space-y-3 pl-1" data-id="element-2620">
                      {tldrItems.map((item, index) => <li key={index} className="flex items-start" data-id="element-2621">
                          <span className="inline-flex flex-shrink-0 bg-primary/10 text-primary font-bold rounded-full w-6 h-6 items-center justify-center text-xs mr-3 mt-0.5" data-id="element-2622">
                            {index + 1}
                          </span>
                          <span className="text-base leading-relaxed" data-id="element-2623">
                            {item}
                          </span>
                        </li>)}
                    </ul>
                  </div>}
              </div>
            </Card>}

          {/* Description Card (if available) */}
          {task?.description && <Card data-id="element-2624">
              <div className="flex items-center justify-between p-4 md:p-5 border-b border-border/40" data-id="element-2625">
                <h2 className="font-semibold text-base md:text-lg flex items-center" data-id="element-2626">
                  <Info size={20} className="mr-2 text-primary" data-id="element-2627" />
                  Description
                </h2>
              </div>
              <div className="p-5" data-id="element-2628">
                <p className="text-base whitespace-pre-wrap leading-relaxed text-muted-foreground" data-id="element-2629">
                  {task.description}
                </p>
              </div>
            </Card>}

          {/* Transcript Card */}
          <Card data-id="element-2630">
            <div className="flex items-center justify-between p-4 md:p-5 border-b border-border/40 sticky top-0 bg-background/95 backdrop-blur z-10" data-id="element-2631">
              <h2 className="font-semibold text-base md:text-lg flex items-center" data-id="element-2632">
                <FileText size={20} className="mr-2 text-primary" data-id="element-2633" />
                Full Transcript
              </h2>
              <div className="flex items-center" data-id="element-2634">
                <Button variant="ghost" size="sm" onClick={() => setShowFullTranscript(!showFullTranscript)} className="text-primary h-9 px-3 rounded-full mr-1" data-id="element-2635">
                  {showFullTranscript ? <ChevronUp size={18} className="mr-1" data-id="element-2636" /> : <ChevronDown size={18} className="mr-1" data-id="element-2637" />}
                  {showFullTranscript ? 'Hide' : 'Show'}
                </Button>
                <Button variant="ghost" size="sm" onClick={handleCopyTranscript} className="text-primary h-9 px-3 rounded-full hidden md:flex" disabled={!transcript} data-id="element-2638">
                  <Copy size={18} className="mr-1" data-id="element-2639" />
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
              </div>
            </div>
            {transcript && transcript.segments.length > 0 ? <>
                {!showFullTranscript ? <div className="p-6 md:p-8" data-id="element-2640">
                    {/* Transcript Preview Section */}
                    <div className="mb-6" data-id="element-2641">
                      <div className="flex items-center gap-2 mb-3" data-id="element-2642">
                        <FileText size={16} className="text-muted-foreground" data-id="element-2643" />
                        <h3 className="text-sm font-medium text-muted-foreground" data-id="element-2644">
                          Transcript preview
                        </h3>
                      </div>
                      <p className="text-base leading-relaxed text-foreground/80 line-clamp-3" data-id="element-2645">
                        {transcript.text.substring(0, 200)}...
                      </p>
                    </div>

                    {/* Stats */}
                    <div className="text-center mb-6" data-id="element-2646">
                      <p className="text-sm text-muted-foreground" data-id="element-2647">
                        The full transcript contains{' '}
                        {transcript.segments.length} segments and approximately{' '}
                        {Math.round(transcript.text.length / 5)} words.
                      </p>
                    </div>

                    {/* Show Button */}
                    <div className="text-center" data-id="element-2648">
                      <Button variant="outline" onClick={() => setShowFullTranscript(true)} className="rounded-full h-11 px-6" data-id="element-2649">
                        <ChevronDown size={18} className="mr-2" data-id="element-2650" />
                        Show Full Transcript
                      </Button>
                    </div>
                  </div> : <div className="p-5 md:p-6 max-h-[600px] md:max-h-[800px] overflow-y-auto scrollbar-hide" data-id="element-2651">
                    <div className="transcript-container space-y-6" data-id="element-2652">
                      {transcript.segments.map(segment => <div key={segment.id} className="group" data-id="element-2653">
                          <div className="text-xs font-mono text-muted-foreground mb-1 opacity-70 group-hover:opacity-100 transition-opacity" data-id="element-2654">
                            <span data-id="element-2655">
                              {Math.floor(segment.start / 60)}:
                              {Math.floor(segment.start % 60).toString().padStart(2, '0')}{' '}
                              -{Math.floor(segment.end / 60)}:
                              {Math.floor(segment.end % 60).toString().padStart(2, '0')}
                            </span>
                          </div>
                          <p className="font-medium text-lg leading-relaxed text-foreground/90" data-id="element-2656">
                            {segment.text}
                          </p>
                        </div>)}
                    </div>
                  </div>}
              </> : <div className="p-8 md:p-12 text-center" data-id="element-2657">
                <p className="text-muted-foreground" data-id="element-2658">
                  {transcript ? 'No transcript segments available.' : 'Transcript data not available.'}
                </p>
              </div>}
          </Card>

          {/* Share Transcript Card */}
          {task && task.task_id && <Card data-id="element-2659">
              <CardContent className="p-5" data-id="element-2660">
                <ShareTranscript taskId={task.task_id} title={task.title || undefined} data-id="element-2661" />
              </CardContent>
            </Card>}

          {/* Research Tools Section */}
          {task && task.task_id && task.status === 'completed' && <ResearchTools taskId={task.task_id} videoId={task.video_id || undefined} data-id="element-2662" />}

          {/* Similar Transcriptions */}
          {similarTranscriptions.length > 0 && <Card className="mt-6" data-id="element-2663">
              <div className="flex items-center justify-between p-4 md:p-5 border-b border-border/40" data-id="element-2664">
                <h2 className="font-semibold text-base md:text-lg" data-id="element-2665">
                  Similar Content
                </h2>
              </div>
              <div className="p-4 md:p-5" data-id="element-2666">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2667">
                  {similarTranscriptions.map(transcription => <div key={transcription.task_id} className="h-full" data-id="element-2668">
                      <TranscriptionCard transcription={transcription} data-id="element-2669" />
                    </div>)}
                </div>
              </div>
            </Card>}
        </div>
      </div>
    </div>;
}